import * as THREE from "three";

// Same constants as PanoramaSession
const DRAG_SENSITIVITY = 0.003;
const PITCH_LIMIT = Math.PI * 0.48;
const DEFAULT_FOV = 75;
const MIN_FOV = 20;
const MAX_FOV = 110;

/**
 * Standalone 360° equirectangular panorama viewer for single captures.
 * Creates its own THREE.js WebGLRenderer / camera / scene and handles
 * drag-to-look and wheel-zoom.
 *
 * Texture technique is identical to {@link EquirectSource}: BackSide sphere +
 * EquirectangularReflectionMapping + horizontal flip.
 */
export class CapturePanoViewer {
  private renderer: THREE.WebGLRenderer;
  private camera: THREE.PerspectiveCamera;
  private scene: THREE.Scene;
  private sphere: THREE.Mesh;
  private material: THREE.MeshBasicMaterial;

  private yaw = 0;
  private pitch = 0;
  private fov = DEFAULT_FOV;
  private raf = 0;

  private dragging = false;
  private lastX = 0;
  private lastY = 0;

  private ro: ResizeObserver;
  private texLoader = new THREE.TextureLoader();
  private currentTex: THREE.Texture | null = null;

  private spinner: HTMLElement;

  constructor(container: HTMLElement) {
    const canvas = document.createElement("canvas");
    canvas.style.cssText =
      "position:absolute;inset:0;width:100%;height:100%;display:block;cursor:grab;";

    this.renderer = new THREE.WebGLRenderer({ canvas, antialias: false });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;

    this.camera = new THREE.PerspectiveCamera(DEFAULT_FOV, 1, 0.01, 200);
    this.camera.rotation.order = "YXZ";

    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(0x050505);

    // BackSide sphere — same geometry as PanoramaSession._ensureSphere()
    const geo = new THREE.SphereGeometry(50, 64, 32);
    this.material = new THREE.MeshBasicMaterial({
      side: THREE.BackSide,
      depthWrite: false,
      color: 0x111111,
    });
    this.sphere = new THREE.Mesh(geo, this.material);
    this.sphere.renderOrder = -1;
    this.scene.add(this.sphere);

    container.appendChild(canvas);

    this.spinner = document.createElement("div");
    this.spinner.className = "ic-spinner";
    this.spinner.style.cssText = "position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);pointer-events:none;";
    container.appendChild(this.spinner);

    const { width, height } = container.getBoundingClientRect();
    this._resize(width || 800, height || 600);

    this.ro = new ResizeObserver((entries) => {
      const e = entries[0];
      if (e) this._resize(e.contentRect.width, e.contentRect.height);
    });
    this.ro.observe(container);

    canvas.addEventListener("pointerdown", this._down);
    canvas.addEventListener("pointermove", this._move);
    canvas.addEventListener("pointerup", this._up);
    canvas.addEventListener("pointercancel", this._up);
    canvas.addEventListener("wheel", this._wheel, { passive: false });

    this._loop();
  }

  loadUrl(url: string): void {
    this.texLoader.loadAsync(url).then((tex) => {
      tex.colorSpace = THREE.SRGBColorSpace;
      tex.minFilter = THREE.LinearFilter;
      tex.magFilter = THREE.LinearFilter;
      tex.mapping = THREE.EquirectangularReflectionMapping;
      // Equirect on BackSide sphere: flip horizontally so east/west read correctly.
      // Same fix as EquirectSource lines 120-122.
      tex.wrapS = THREE.RepeatWrapping;
      tex.repeat.x = -1;
      tex.offset.x = 1;

      const prev = this.currentTex;
      this.currentTex = tex;
      this.material.map = tex;
      this.material.color.setHex(0xffffff);
      this.material.needsUpdate = true;
      this.spinner.remove();

      if (prev) prev.dispose();
    }).catch((err) => {
      console.warn("[CapturePanoViewer] texture load failed:", err);
    });
  }

  dispose(): void {
    cancelAnimationFrame(this.raf);
    this.ro.disconnect();
    const canvas = this.renderer.domElement;
    canvas.removeEventListener("pointerdown", this._down);
    canvas.removeEventListener("pointermove", this._move);
    canvas.removeEventListener("pointerup", this._up);
    canvas.removeEventListener("pointercancel", this._up);
    canvas.removeEventListener("wheel", this._wheel);
    this.sphere.geometry.dispose();
    this.material.dispose();
    this.currentTex?.dispose();
    this.renderer.dispose();
  }

  // ── Render loop ───────────────────────────────────────────────────────────

  private _loop = (): void => {
    this.raf = requestAnimationFrame(this._loop);
    this.camera.rotation.set(this.pitch, this.yaw, 0);
    this.camera.fov = this.fov;
    this.camera.updateProjectionMatrix();
    this.renderer.render(this.scene, this.camera);
  };

  private _resize(w: number, h: number): void {
    if (w < 1 || h < 1) return;
    this.renderer.setSize(w, h, false);
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();
  }

  // ── Pointer + wheel ───────────────────────────────────────────────────────

  private _down = (e: PointerEvent): void => {
    if (e.button !== 0) return;
    this.dragging = true;
    this.lastX = e.clientX;
    this.lastY = e.clientY;
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    (e.currentTarget as HTMLElement).style.cursor = "grabbing";
  };

  private _move = (e: PointerEvent): void => {
    if (!this.dragging) return;
    const dx = e.clientX - this.lastX;
    const dy = e.clientY - this.lastY;
    this.lastX = e.clientX;
    this.lastY = e.clientY;
    this.yaw += dx * DRAG_SENSITIVITY;
    this.pitch = Math.max(
      -PITCH_LIMIT,
      Math.min(PITCH_LIMIT, this.pitch + dy * DRAG_SENSITIVITY),
    );
  };

  private _up = (e: PointerEvent): void => {
    if (!this.dragging) return;
    this.dragging = false;
    try { (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId); } catch { /* noop */ }
    (e.currentTarget as HTMLElement).style.cursor = "grab";
  };

  private _wheel = (e: WheelEvent): void => {
    e.preventDefault();
    this.fov = Math.max(MIN_FOV, Math.min(MAX_FOV, this.fov + e.deltaY * 0.05));
  };
}
